import {
  queryOptions,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { z } from 'zod';
import i18n from '@/integrations/react-i18next/i18n';
import { smartFormApi } from '@/services';
import type { QueryOptions } from '@/types/tanstack-query-options';
import type { paths } from '~/generated/smart-form/api-schema';
import { UNCATEGORIZED } from '../components/user-widget/consts';

const PATH = '/v1/component/queryAllCategoryListAndComponent' as const;

type QueryUserWidgets = paths[typeof PATH];
type QueryUserWidgetsResponse =
  QueryUserWidgets['get']['responses']['200']['content']['*/*'];

const userWidgetSchema = z.object({
  id: z.string(),
  formComponentName: z.string(),
  formComponentCategoryId: z.string().nullable(),
  sort: z.number(),
  lockVersion: z.number(),
});

const userWidgetCategorySchema = z.object({
  id: z.string(),
  categoryName: z.string(),
  parentId: z.string().nullable(),
  componentList: z.array(userWidgetSchema),
  sort: z.number(),
  lockVersion: z.number(),
});

type UserWidgetsWithSubCategories = z.infer<typeof userWidgetCategorySchema> & {
  subCategoryList: UserWidgetsWithSubCategories[];
};

const userWidgetCategoryWithSubCategoriesSchema: z.ZodType<UserWidgetsWithSubCategories> =
  z.lazy(() =>
    userWidgetCategorySchema.extend({
      subCategoryList: z.array(userWidgetCategoryWithSubCategoriesSchema),
    }),
  );

const userWidgetsSchema = z.array(userWidgetCategoryWithSubCategoriesSchema);

export type UserWidgets = z.infer<typeof userWidgetsSchema>;

const getUserWidgets = async (signal: AbortSignal) => {
  const { data: resp } = await smartFormApi.get<QueryUserWidgetsResponse>(
    PATH,
    {
      signal,
    },
  );

  // NOTE: Handle uncategorized items by assigning a safe default and setting up an 18n name.
  const data = resp.data?.map((item) =>
    item.id === null && item.categoryName === UNCATEGORIZED
      ? {
          ...item,
          lockVersion: 1,
          id: UNCATEGORIZED,
          categoryName: i18n.t('backend-i18n:smart_form_uncategorized'),
        }
      : item,
  );
  const result = userWidgetsSchema.safeParse(data);
  if (result.error) {
    throw new Error(result.error.message);
  }

  return result.data;
};

export const getUserWidgetsQueryOptions = () =>
  queryOptions({
    queryKey: [PATH],
    queryFn: ({ signal }) => getUserWidgets(signal),
  });

export const useQueryUserWidgets = (options?: QueryOptions<UserWidgets>) => {
  return useQuery({
    queryKey: [PATH],
    queryFn: ({ signal }) => getUserWidgets(signal),
    ...options,
  });
};

export const useSuspenseQueryUserWidgets = () => {
  const { data } = useSuspenseQuery(getUserWidgetsQueryOptions());
  return { data };
};

export { PATH as QUERY_USER_WIDGETS_KEY };
